import { Injectable, computed, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/browser';
import { CirculationGroupGateway } from '@wilson/api/gateway';
import { CirculationGroupWithShiftAndStay } from '@wilson/interfaces';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { GroupItemsSelectionHelper } from './group-items-selection-helper.interface';

@Injectable({
  providedIn: 'root',
})
export class CirculationGroupItemsSelectionHelper
  implements GroupItemsSelectionHelper
{
  private readonly circulationGroupGateway = inject(CirculationGroupGateway);
  private readonly messageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  private readonly selectedItemsSignal = signal<
    CirculationGroupWithShiftAndStay[]
  >([]);

  private operationQueue: Promise<void> = Promise.resolve();

  public readonly groupedShiftIds = computed(() =>
    this.toElementIdSet(this.selectedItemsSignal(), 'shiftId'),
  );

  public readonly groupedStayIds = computed(() =>
    this.toElementIdSet(this.selectedItemsSignal(), 'stayId'),
  );

  public async selectGroupItems(elementId: string): Promise<void> {
    await this.queueOperation(async () => {
      const groupItems = await this.getGroupItems(elementId);
      this.selectedItemsSignal.set(groupItems);
    });
  }

  public async updateGroupItemsSelection(elementId: string): Promise<void> {
    await this.queueOperation(async () => {
      const groupItems = await this.getGroupItems(elementId);
      this.selectedItemsSignal.set(
        this.mergeUniqueItems([...this.selectedItemsSignal(), ...groupItems]),
      );
    });
  }

  public async syncGroupItemsFromElementIds(
    elementIds: string[],
  ): Promise<void> {
    await this.queueOperation(async () => {
      if (elementIds.length === 0) {
        this.selectedItemsSignal.set([]);
      } else {
        const groupItemsPerElement = await Promise.all(
          elementIds.map((elementId) => this.getGroupItems(elementId)),
        );
        this.selectedItemsSignal.set(
          this.mergeUniqueItems(groupItemsPerElement.flat()),
        );
      }
    });
  }

  public clearSelectedItems(): Promise<void> {
    return this.queueOperation(async () => {
      this.selectedItemsSignal.set([]);
    });
  }

  public getCachedGroupItemsForElement(
    elementId: string,
  ): CirculationGroupWithShiftAndStay[] | null {
    const items = this.selectedItemsSignal();
    if (items.length === 0) {
      return null;
    }

    const elementIsInCachedGroup = items.some(
      (item) => item.shiftId === elementId || item.stayId === elementId,
    );

    return elementIsInCachedGroup ? items : null;
  }

  private queueOperation(operation: () => Promise<void>): Promise<void> {
    const queuedOperation = this.operationQueue.then(operation);
    this.operationQueue = queuedOperation.catch(() => undefined);
    return queuedOperation;
  }

  private toElementIdSet(
    items: CirculationGroupWithShiftAndStay[],
    key: 'shiftId' | 'stayId',
  ): ReadonlySet<string> {
    const ids = new Set<string>();
    for (const item of items) {
      const id = item[key];
      if (id) {
        ids.add(id);
      }
    }
    return ids;
  }

  private mergeUniqueItems(
    items: CirculationGroupWithShiftAndStay[],
  ): CirculationGroupWithShiftAndStay[] {
    const seenIds = new Set<string>();
    return items.filter((item) => {
      if (seenIds.has(item.id)) {
        return false;
      }
      seenIds.add(item.id);
      return true;
    });
  }

  private async getGroupItems(
    elementId: string,
  ): Promise<CirculationGroupWithShiftAndStay[]> {
    try {
      return await firstValueFrom(
        this.circulationGroupGateway.getItems(elementId),
      );
    } catch (error) {
      captureException(error);
      this.messageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      return [];
    }
  }
}
